import { Button, Chip, faDigits, Field, formatDate, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { type DocumentTypeRow, hrApi, type PersonDocumentRow } from "../../api.js";
import { askReason, ErrorLine, useAction } from "../common.js";

export function DocumentsTab() {
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [jobResult, setJobResult] = useState<string | null>(null);

  // Form state: Add Document Type
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const documentsQuery = useQuery({
    queryKey: ["hr", "documents"],
    queryFn: () => hrApi.listDocuments(),
  });

  const docTypesQuery = useQuery({
    queryKey: ["hr", "document-types"],
    queryFn: () => hrApi.listDocumentTypes(),
  });

  const peopleQuery = useQuery({
    queryKey: ["hr", "people"],
    queryFn: () => hrApi.listPeople(),
  });

  const peopleNameMap = new Map<string, string>();
  for (const p of peopleQuery.data ?? []) {
    peopleNameMap.set(p.personId, p.personName);
  }

  const runJobAction = useAction(async () => {
    const res = await hrApi.runExpiryJob();
    setJobResult(`بررسی با موفقیت انجام شد. تعداد ${faDigits(res.notifiedCount)} اعلان صادر شد.`);
  }, [["hr", "documents"]]);

  const addTypeAction = useAction(async () => {
    await hrApi.createDocumentType({
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      active: true,
    });
    setShowAddTypeModal(false);
    setCode("");
    setName("");
    setDescription("");
  }, [["hr", "document-types"]]);

  const archiveDocAction = useAction(
    async (doc: PersonDocumentRow) => {
      const reason = askReason(`مدرک ${doc.kindLabel || doc.kind}`);
      if (!reason) return;
      await hrApi.archiveDocument(doc.id, doc.version, reason);
    },
    [["hr", "documents"]],
  );

  const archiveTypeAction = useAction(
    async (dt: DocumentTypeRow) => {
      const reason = askReason(`نوع مدرک ${dt.name}`);
      if (!reason) return;
      await hrApi.archiveDocumentType(dt.id, dt.version, reason);
    },
    [["hr", "document-types"]],
  );

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Expiry Job Control Banner */}
      <div className="card" style={{ padding: "var(--s3) var(--s4)" }}>
        <div className="row wrap-between" style={{ alignItems: "center" }}>
          <div>
            <h3 className="t-title">سیستم پایش خودکار انقضای مدارک</h3>
            <p className="muted t-cap" style={{ marginTop: "2px" }}>
              ارسال اعلان‌های پیش از انقضا (پیش‌فرض ۳۰ روز قبل و روز انقضا) به مدیران منابع انسانی
            </p>
          </div>
          <div className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
            {jobResult && (
              <span className="bold" style={{ color: "var(--ok, #16a34a)", fontSize: "14px" }}>
                {jobResult}
              </span>
            )}
            <Button
              variant="default"
              onClick={() => {
                runJobAction.run(undefined);
              }}
              disabled={runJobAction.busy}
            >
              {runJobAction.busy ? "در حال پایش..." : "اجرای بررسی انقضاها"}
            </Button>
          </div>
        </div>
      </div>

      {/* All Documents Table */}
      <Panel title="مدارک پرسنلی و وضعیت انقضا">
        <div className="card" style={{ padding: "0", overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>نام پرسنل</th>
                <th>نوع مدرک</th>
                <th>شماره مدرک</th>
                <th>تاریخ صدور</th>
                <th>تاریخ انقضا</th>
                <th>وضعیت اعتبار</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(documentsQuery.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: "var(--s4)" }} className="muted">
                    هنوز مدرکی در سامانه ثبت نشده است.
                  </td>
                </tr>
              ) : (
                (documentsQuery.data ?? []).map((doc) => {
                  const personName = peopleNameMap.get(doc.personId) || doc.personId;
                  const isExpired = doc.expiryStatus === "expired";
                  const isSoon = doc.expiryStatus === "expiring_soon";

                  return (
                    <tr key={doc.id}>
                      <td className="bold">{personName}</td>
                      <td>{doc.kindLabel || doc.kind}</td>
                      <td className="num">{faDigits(doc.documentNumber)}</td>
                      <td className="num">{doc.issueDate ? formatDate(doc.issueDate) : "—"}</td>
                      <td className="num">{formatDate(doc.expiryDate)}</td>
                      <td>
                        {isExpired ? (
                          <span
                            className="chip coral"
                            style={{ color: "var(--coral-ink)", background: "var(--coral-soft)" }}
                          >
                            منقضی
                          </span>
                        ) : isSoon ? (
                          <span
                            className="chip coral"
                            style={{ color: "var(--coral-ink)", background: "var(--coral-soft)" }}
                          >
                            انقضا نزدیک است
                          </span>
                        ) : (
                          <Chip tone="ok">معتبر</Chip>
                        )}
                      </td>
                      <td className="end">
                        <Button
                          variant="ghost"
                          size="md"
                          onClick={() => {
                            archiveDocAction.run(doc);
                          }}
                          disabled={archiveDocAction.busy}
                        >
                          حذف
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Document Types Configuration */}
      <Panel
        title="انواع مدرک (دسته‌بندی‌ها)"
        actions={
          <Button
            variant="default"
            onClick={() => {
              setShowAddTypeModal(true);
            }}
          >
            افزودن نوع مدرک جدید
          </Button>
        }
      >
        <div className="card" style={{ padding: "0", overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>شناسه</th>
                <th>عنوان</th>
                <th>توضیحات</th>
                <th>وضعیت</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(docTypesQuery.data ?? []).map((dt) => (
                <tr key={dt.id}>
                  <td className="num" dir="ltr" style={{ textAlign: "start" }}>
                    {dt.code}
                  </td>
                  <td className="bold">{dt.name}</td>
                  <td className="muted t-cap">{dt.description || "—"}</td>
                  <td>{dt.active ? <Chip tone="ok">فعال</Chip> : <Chip plain>غیرفعال</Chip>}</td>
                  <td className="end">
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={() => {
                        archiveTypeAction.run(dt);
                      }}
                      disabled={archiveTypeAction.busy}
                    >
                      بایگانی
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Modal: Add Document Type */}
      {showAddTypeModal && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "480px", width: "95%" }}>
            <div className="row wrap-between" style={{ alignItems: "center", marginBottom: "var(--s3)" }}>
              <h2 className="t-title">افزودن نوع مدرک جدید</h2>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowAddTypeModal(false);
                }}
              >
                بستن
              </Button>
            </div>

            <form
              className="stack"
              style={{ gap: "var(--s3)" }}
              onSubmit={(e) => {
                e.preventDefault();
                addTypeAction.run(undefined);
              }}
            >
              <Field
                id="type-code"
                label="کد انگلیسی یکتا"
                dir="ltr"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                required
                placeholder="مثال: fire_safety_cert"
              />

              <Field
                id="type-name"
                label="عنوان نوع مدرک"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                required
                placeholder="مثال: گواهی ایمنی و آتش‌نشانی"
              />

              <Field
                id="type-desc"
                label="شرح (اختیاری)"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
              />

              <ErrorLine error={addTypeAction.error} />

              <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddTypeModal(false);
                  }}
                  disabled={addTypeAction.busy}
                >
                  انصراف
                </Button>
                <Button variant="primary" type="submit" disabled={addTypeAction.busy}>
                  {addTypeAction.busy ? "در حال ثبت..." : "ثبت نوع مدرک"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
